import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Spacer,
  Tooltip,
} from "@chakra-ui/react";
import { Glossary } from "src/components/Text/Glossary";
import { useGlossary } from "src/hooks/useGlossary";
import { PASSWORD_MANAGER_IGNORE } from "src/types/passwordManager";
import type { NewStudentEntry } from "./useStudentEntries";

interface NewStudentFormProps {
  entry: NewStudentEntry;
  onChange: (patch: Partial<NewStudentEntry>) => void;
  onConfirm: () => void;
  onRemove: () => void;
}

// Controlled editor for one new student in the create-family payload. Every edit
// writes straight to the entry. "Add student" collapses it into a chip (still in
// the payload, created on Save); "Cancel" removes it.
export function NewStudentForm({
  entry,
  onChange,
  onConfirm,
  onRemove,
}: NewStudentFormProps) {
  const { glossaryTransform } = useGlossary();
  // Require the basics before collapsing into a chip so we never confirm an empty one.
  const canConfirm =
    entry.firstName.trim() !== "" &&
    entry.lastName.trim() !== "" &&
    !!entry.birthDate;
  return (
    <Flex direction="column" gap={4}>
      <Grid gridTemplateColumns="repeat(auto-fit, minmax(14rem, 1fr))" gap={3}>
        <FormControl>
          <FormLabel>First name</FormLabel>
          <Input
            placeholder="E.g: Alex"
            {...PASSWORD_MANAGER_IGNORE}
            value={entry.firstName}
            onChange={(event) => onChange({ firstName: event.target.value })}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Middle name (Optional)</FormLabel>
          <Input
            placeholder="E.g: Lin"
            {...PASSWORD_MANAGER_IGNORE}
            value={entry.middleName ?? ""}
            onChange={(event) =>
              onChange({ middleName: event.target.value || null })
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>Last name</FormLabel>
          <Input
            placeholder="E.g: Doe"
            {...PASSWORD_MANAGER_IGNORE}
            value={entry.lastName}
            onChange={(event) => onChange({ lastName: event.target.value })}
          />
        </FormControl>
      </Grid>

      <Grid gridTemplateColumns="repeat(auto-fit, minmax(14rem, 1fr))" gap={3}>
        <FormControl>
          <FormLabel>Date of birth</FormLabel>
          <Input
            type="date"
            {...PASSWORD_MANAGER_IGNORE}
            value={entry.birthDate ?? ""}
            onChange={(event) =>
              onChange({ birthDate: event.target.value || null })
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>Reference ID (Optional)</FormLabel>
          <Input
            placeholder="E.g.: OE12345678122"
            {...PASSWORD_MANAGER_IGNORE}
            value={entry.referenceId ?? ""}
            onChange={(event) =>
              onChange({ referenceId: event.target.value || null })
            }
          />
        </FormControl>
      </Grid>

      <Checkbox
        isChecked={entry.useParentAddress}
        onChange={(event) =>
          onChange({ useParentAddress: event.target.checked })
        }
      >
        <Glossary>Use same address as Parent/guardian's address</Glossary>
      </Checkbox>

      {!entry.useParentAddress && (
        <Box>
          <Grid
            gridTemplateColumns="repeat(auto-fit, minmax(14rem, 1fr))"
            gap={3}
          >
            <FormControl>
              <FormLabel>Address</FormLabel>
              <Input
                placeholder="E.g.: 532 Oakland Ave"
                {...PASSWORD_MANAGER_IGNORE}
                value={entry.streetAddress ?? ""}
                onChange={(event) =>
                  onChange({ streetAddress: event.target.value || null })
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Apartment/suite (Optional)</FormLabel>
              <Input
                placeholder="E.g.: 5B"
                {...PASSWORD_MANAGER_IGNORE}
                value={entry.streetAddressLine2 ?? ""}
                onChange={(event) =>
                  onChange({ streetAddressLine2: event.target.value || null })
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>City</FormLabel>
              <Input
                placeholder="E.g: Springfield"
                {...PASSWORD_MANAGER_IGNORE}
                value={entry.city ?? ""}
                onChange={(event) =>
                  onChange({ city: event.target.value || null })
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>State</FormLabel>
              <Input
                placeholder="E.g: CA"
                {...PASSWORD_MANAGER_IGNORE}
                value={entry.state ?? ""}
                onChange={(event) =>
                  onChange({ state: event.target.value || null })
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Zip code</FormLabel>
              <Input
                placeholder="E.g: 94100"
                {...PASSWORD_MANAGER_IGNORE}
                value={entry.zipCode ?? ""}
                onChange={(event) =>
                  onChange({ zipCode: event.target.value || null })
                }
              />
            </FormControl>
          </Grid>
        </Box>
      )}

      <Flex direction="row" align="center" gap={2}>
        <Spacer />
        <Button type="button" variant="ghost" onClick={onRemove}>
          Cancel
        </Button>
        <Tooltip
          label={glossaryTransform(
            "Student may be added, but won't be created until you Save"
          )}
          shouldWrapChildren
        >
          <Button type="button" onClick={onConfirm} isDisabled={!canConfirm}>
            <Glossary>Add student</Glossary>
          </Button>
        </Tooltip>
      </Flex>
    </Flex>
  );
}
